import { Link, Navigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Mail } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { getPost, POSTS } from "@/lib/posts";
import { formatDate } from "@/lib/markdown";
import { articleJsonLd, breadcrumbJsonLd, websiteJsonLd, BUSINESS, DOCTORS, categoryImage } from "@/data/site";

const components = {
  h2: ({ children }) => (
    <h2 className="font-display text-2xl md:text-4xl tracking-tight text-ink mt-14 mb-5">
      {children}
    </h2>
  ),
  p: ({ children }) => (
    <p className="text-base md:text-lg leading-relaxed text-ink/80 mb-6">{children}</p>
  ),
  ul: ({ children }) => <ul className="space-y-3 mb-8">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal space-y-3 pl-6 mb-8">{children}</ol>,
  li: ({ children }) => (
    <li className="flex gap-3 text-base md:text-lg leading-relaxed text-ink/80 before:content-[''] before:mt-3 before:h-1.5 before:w-1.5 before:rotate-45 before:bg-clinic before:shrink-0 [&>p]:mb-0">
      {children}
    </li>
  ),
  blockquote: ({ children }) => (
    <div
      data-testid="post-answer-block"
      className="border-l-4 border-clinic bg-clinic-tint p-6 md:p-8 mb-10 [&_p]:mb-0 [&_p]:text-ink"
    >
      {children}
    </div>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto mb-10">
      <table className="w-full border-collapse text-sm md:text-base">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border border-ink/15 bg-clinic-tint px-4 py-3 text-left font-mono text-[11px] tracking-[0.15em] uppercase text-ink">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-ink/15 px-4 py-3 text-ink/80">{children}</td>
  ),
  strong: ({ children }) => <strong className="text-ink font-semibold">{children}</strong>,
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);

  if (!post) return <Navigate to="/blog" replace />;

  const others = POSTS.filter((p) => p.slug !== slug).slice(0, 2);
  const doctor = DOCTORS.find((d) => d.id === post.authorId);

  return (
    <>
      <Seo
        title={`${post.title} | BPMedLav Roma`}
        description={post.description}
        path={`/blog/${post.slug}`}
        image={categoryImage(post.category)}
        jsonLd={[
          websiteJsonLd(),
          articleJsonLd(post),
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Blog", path: "/blog" },
            { name: post.title, path: `/blog/${post.slug}` },
          ]),
        ]}
      />
      <main className="bg-paper pt-28 md:pt-36 pb-16">
        <article className="mx-auto max-w-3xl px-5 md:px-8" data-testid="blog-post">
          <Reveal>
            <Link
              to="/blog"
              data-testid="post-back-link"
              className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-ink/50 hover:text-clinic transition-colors mb-10"
            >
              <ArrowLeft size={14} /> Blog
            </Link>
            <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-clinic mb-5">
              {post.category}
            </p>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight text-ink mb-8">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 border-y border-ink/10 py-4 mb-12">
              <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-ink/60">
                {post.author}
              </p>
              <span
                data-testid="post-updated-badge"
                className="rounded-full bg-clinic-tint px-3 py-1 font-mono text-[10px] tracking-[0.15em] uppercase text-clinic"
              >
                Ultimo aggiornamento: {formatDate(post.dateModified)}
              </span>
              <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-ink/40">
                {post.readingTime} di lettura
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <img
              src={categoryImage(post.category)}
              alt={`Medicina del lavoro — ${post.category}`}
              className="h-56 md:h-80 w-full object-cover border border-ink/10 mb-12"
            />
          </Reveal>

          <Reveal delay={0.1}>
            <div className="md-body">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                {post.body}
              </ReactMarkdown>
            </div>
          </Reveal>

          {doctor && (
            <Reveal delay={0.1}>
              <div
                data-testid="post-author-box"
                className="mt-14 flex items-center gap-5 border border-ink/10 p-6 md:p-8"
              >
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-clinic-tint font-display text-xl text-clinic">
                  {doctor.name.split(" ").slice(-1)[0][0]}
                </div>
                <div>
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-clinic mb-1">
                    Scritto e revisionato da
                  </p>
                  <p className="font-display text-lg text-ink">{doctor.name}</p>
                  <p className="text-sm text-ink/60">
                    {doctor.jobTitle} · {BUSINESS.shortName}
                  </p>
                </div>
              </div>
            </Reveal>
          )}

          <Reveal delay={0.1}>
            <div className="mt-16 bg-ink p-8 md:p-12 text-center">
              <p className="font-display text-2xl md:text-3xl text-paper mb-6">
                Vuoi mettere in regola la tua azienda?
              </p>
              <a
                href={`mailto:${BUSINESS.email}?subject=Richiesta%20informazioni%20—%20${encodeURIComponent(post.title)}`}
                data-testid="post-cta-email"
                className="inline-flex items-center gap-2 rounded-full bg-clinic px-8 py-4 font-mono text-[11px] tracking-[0.15em] uppercase text-white transition-transform hover:-translate-y-0.5"
              >
                <Mail size={14} /> Richiedi preventivo via email
              </a>
            </div>
          </Reveal>
        </article>

        <aside className="mx-auto max-w-3xl px-5 md:px-8 mt-14">
          <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-clinic mb-6">
            Continua a leggere
          </p>
          <div className="grid gap-px bg-ink/10 border border-ink/10 sm:grid-cols-2">
            {others.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`related-post-${p.slug}`}
                className="group bg-paper transition-colors hover:bg-clinic-tint/60"
              >
                <div className="h-32 overflow-hidden border-b border-ink/10">
                  <img
                    src={categoryImage(p.category)}
                    alt={`Medicina del lavoro — ${p.category}`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-7">
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-clinic mb-3">
                    {p.category}
                  </p>
                  <p className="font-display text-lg md:text-xl leading-snug text-ink group-hover:text-clinic transition-colors">
                    {p.title}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </aside>
      </main>
    </>
  );
}
